import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Radio,
    RadioGroup,
    FormControlLabel,
    Alert,
    Divider,
    CircularProgress,
    Paper
} from '@mui/material';
import {
    CheckCircle,
    Token,
    Storage,
    AccessTime,
    Star,
    TrendingUp,
    Close,
    Send
} from '@mui/icons-material';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import ApiUrls from '../../../helper/ApiUrls';
import { API } from '../../../services/axios.service';

const PlanUpgradeDialog = ({ 
    open, 
    onClose, 
    onSubmitRequest, 
    membershipData = null,
    loading = false 
}) => {
    const [availablePlans, setAvailablePlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [confirmationStep, setConfirmationStep] = useState(false);
    const [fetchingPlans, setFetchingPlans] = useState(true);
    const currentPlan = membershipData?.membership||null;
    const user = useSelector(state => state.user.data);

    useEffect(()=>{
        if (open) {
            setFetchingPlans(true);
            API.get(ApiUrls.GET_MEMBERSHIP_PLANS).then(res=>{
                const plans = res.data.data;
                console.log('currentPlans',plans);
                const filteredPlans = plans.filter(plan => {
                    if (!currentPlan) return true;
                    // Only show plans that are upgrades (higher price or better features)
                    const currentPrice = currentPlan.monthly_price || currentPlan.one_time_price || 0;
                    const planPrice = plan.monthly_price || plan.one_time_price || 0;
                    return planPrice > currentPrice && plan.id !== currentPlan.id;
                });
                setAvailablePlans(filteredPlans);
            }).catch(err=>{
                console.log('Error fetching plans',err);
            }).finally(()=>{
                setFetchingPlans(false);
            })
        }
    },[open,currentPlan]);

    const selectedPlan = availablePlans.find(plan => plan.id === selectedPlanId);

    const handlePlanSelect = (planId) => {
        setSelectedPlanId(planId);
    };

    const handleNext = () => {
        if (selectedPlanId) {
            setConfirmationStep(true);
        }
    };

    const handleBack = () => {
        setConfirmationStep(false);
    };

    const handleSubmitUpgradeRequest = async () => {
        if (selectedPlan && onSubmitRequest) {
            const requestData = {
                requested_plan_id: selectedPlan.id,
                current_plan_id: currentPlan?.id || null,
                user_id: user.id
            };
            
            await onSubmitRequest(requestData);
            handleClose();
        }
    };

    const handleClose = () => {
        setSelectedPlanId('');
        setConfirmationStep(false);
        onClose();
    };

    const formatPrice = (plan) => {
        if (plan.monthly_price) {
            return `$${plan.monthly_price}/month`;
        }
        return `$${plan.one_time_price} one-time`;
    };

    const getPlanBadgeColor = (badge) => {
        switch (badge) {
            case 'Most Popular': return 'primary';
            case 'Premium': return 'secondary';
            default: return 'default';
        }
    };

    if (fetchingPlans) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Loading available plans...</Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="lg" 
            fullWidth
            PaperProps={{
                sx: { minHeight: '70vh' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h5" component="div">
                            {confirmationStep ? 'Confirm Upgrade Request' : 'Choose Your Plan'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {confirmationStep 
                                ? 'Review your selection and submit for admin approval'
                                : 'Select a plan to upgrade your current membership'
                            }
                        </Typography>
                    </Box>
                    <Button
                        onClick={handleClose}
                        sx={{ minWidth: 'auto', p: 1 }}
                    >
                        <Close />
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent>
                {!confirmationStep ? (
                    /* Plan Selection Step */
                    <Box>
                        {/* Current Plan Info */}
                        {currentPlan && (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                <Typography variant="subtitle2">
                                    Current Plan: <strong>{currentPlan.name}</strong> - 
                                    ${currentPlan.monthly_price || currentPlan.one_time_price}
                                    {currentPlan.monthly_price ? '/month' : ' one-time'}
                                </Typography>
                            </Alert>
                        )}

                        {availablePlans.length === 0 ? (
                            <Alert severity="warning">
                                <Typography variant="h6">No Upgrade Options Available</Typography>
                                <Typography>You're already on the highest available plan or there are no plans available for upgrade.</Typography>
                            </Alert>
                        ) : (
                            <RadioGroup
                                value={selectedPlanId}
                                onChange={(e) => handlePlanSelect(e.target.value)}
                            >
                                <Grid container spacing={3}>
                                    {availablePlans.map((plan) => (
                                        <Grid item size={4} key={plan.id}>
                                            <Card 
                                                sx={{ 
                                                    height: '100%',
                                                    cursor: 'pointer',
                                                    border: selectedPlanId === plan.id ? 2 : 1,
                                                    borderColor: selectedPlanId === plan.id ? 'primary.main' : 'divider',
                                                    position: 'relative',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        boxShadow: 2
                                                    }
                                                }}
                                                onClick={() => handlePlanSelect(plan.id)}
                                            >
                                                {/* Plan Badge */}
                                                {plan.badge && (
                                                    <Box sx={{ position: 'absolute', top: -8, right: 16, zIndex: 1 }}>
                                                        <Chip 
                                                            label={plan.badge}
                                                            color={getPlanBadgeColor(plan.badge)}
                                                            size="small"
                                                            icon={plan.recommended ? <Star fontSize="small" /> : undefined}
                                                        />
                                                    </Box>
                                                )}

                                                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                        <Radio
                                                            checked={selectedPlanId === plan.id}
                                                            value={plan.id}
                                                            sx={{ mr: 1 }}
                                                        />
                                                        <Typography variant="h6">
                                                            {plan.name}
                                                        </Typography>
                                                    </Box>

                                                    <Typography variant="h4" color="primary" gutterBottom>
                                                        {formatPrice(plan)}
                                                    </Typography>

                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        {plan.validity_in_days} days validity
                                                    </Typography>

                                                    <Divider sx={{ my: 2 }} />

                                                    {/* Plan Limits */}
                                                    <Box sx={{ mb: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                            <Token fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                                            <Typography variant="body2">
                                                                {plan.token_limit?.toLocaleString() || 'Unlimited'} tokens
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                            <Storage fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                                            <Typography variant="body2">
                                                                {plan.knowledge_size_limit || 'Unlimited'} GB storage
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <AccessTime fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                                            <Typography variant="body2">
                                                                {plan.validity_in_days} days access
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* Features List */}
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            Features:
                                                        </Typography>
                                                        <List dense sx={{ p: 0 }}>
                                                            {plan.features?.map((feature, index) => (
                                                                <ListItem key={index} sx={{ px: 0, py: 0.25 }}>
                                                                    <ListItemIcon sx={{ minWidth: 20 }}>
                                                                        <CheckCircle fontSize="small" color="success" />
                                                                    </ListItemIcon>
                                                                    <ListItemText 
                                                                        primary={feature}
                                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </RadioGroup>
                        )}
                    </Box>
                ) : (
                    /* Confirmation Step */
                    <Box>
                        <Alert severity="success" sx={{ mb: 3 }}>
                            <Typography variant="h6">Upgrade Request Summary</Typography>
                            <Typography>Your request will be sent to admin for approval. You'll be notified once it's processed.</Typography>
                        </Alert>

                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Grid container spacing={3}>
                                <Grid item size={6}>
                                    <Typography variant="h6" gutterBottom>Current Plan</Typography>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {currentPlan?.name || 'No Active Plan'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {currentPlan ? formatPrice(currentPlan) : 'Free'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item size={6}>
                                    <Typography variant="h6" gutterBottom>
                                        <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
                                        Requested Plan
                                    </Typography>
                                    <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
                                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                            {selectedPlan?.name}
                                        </Typography>
                                        <Typography variant="body2" color="primary.dark">
                                            {selectedPlan ? formatPrice(selectedPlan) : ''}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>

                        {selectedPlan && (
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>Plan Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item size={4}>
                                        <Typography variant="body2" color="text.secondary">Tokens</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {selectedPlan.token_limit?.toLocaleString() || 'Unlimited'}
                                        </Typography>
                                    </Grid>
                                    <Grid item size={4}>
                                        <Typography variant="body2" color="text.secondary">Storage</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {selectedPlan.knowledge_size_limit || 'Unlimited'} GB
                                        </Typography>
                                    </Grid>
                                    <Grid item size={4}>
                                        <Typography variant="body2" color="text.secondary">Validity</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {selectedPlan.validity_in_days} days
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: confirmationStep ? 'space-between' : 'flex-end' }}>
                    {confirmationStep && (
                        <Button onClick={handleBack} variant="outlined">
                            Back
                        </Button>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleClose}>
                            Cancel
                        </Button>
                        
                        {!confirmationStep ? (
                            <Button 
                                variant="outlined" 
                                onClick={handleNext}
                                disabled={!selectedPlanId || availablePlans.length === 0}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button 
                                variant="outlined" 
                                onClick={handleSubmitUpgradeRequest}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={16} /> : <Send />}
                            >
                                {loading ? 'Submitting...' : 'Submit Request'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default PlanUpgradeDialog;